import { type EditorView } from 'prosemirror-view'
import TrackedChangesMarkViewsStyles from 'common/Prose/nodeViews/TrackedChanges/TrackedChanges.module.scss'
import { find } from 'lodash'

// NOTE: resolves the last element that wraps text at the beginning of a node, e.g.
// <p><span><b>text</b></span></p> returns the <b>
// BUT: it ignores any node that has `data-presence-cursor='true'` (or any truthy value)
// and will in that case continue down the next child's node.
// <p><span data-presence-cursor="true">text</span><span><b>text</b></span></p> returns the `<b>`
const getFirstLastDescendantElement = (element: HTMLElement): HTMLElement | undefined => {
  if (!element?.firstChild) return element
  if ((element.firstChild as HTMLElement).dataset?.nodeType === 'smartLink') return element.firstChild as HTMLElement

  const childElement =
    element.firstChild && (element.firstChild as HTMLElement).dataset?.presenceCursor
      ? (element.children[1] as HTMLElement)
      : (element.firstChild as HTMLElement)

  if (childElement && childElement.nodeType !== Node.TEXT_NODE) {
    return getFirstLastDescendantElement(childElement)
  }

  return element
}

const IGNORED_SELECTORS = `.ProseMirror a, .ProseMirror .${TrackedChangesMarkViewsStyles.insertion}, .ProseMirror .${TrackedChangesMarkViewsStyles.deletion}`

export const updateListItemMarkerStyles = (
  view: EditorView,
  pos: number,
  contentDOM: HTMLElement,
  dom: HTMLElement,
) => {
  if (!pos) return

  const node = view.state.doc.resolve(pos).nodeAfter
  if (!node?.firstChild?.isTextblock) return

  const firstRealContentDOMChild = find(contentDOM.children, (el) => !el.classList.contains('ProseMirror-widget'))
  if (!firstRealContentDOMChild) return

  const wrapperStyle = window.getComputedStyle(firstRealContentDOMChild as Element)

  // Disable PM's DOMObserver before setting styles so we do not disturb PM. (e.g. it might want to sync the selection from DOM)
  ;(view as any).domObserver.stop()

  // NOTE: get spacing styles from first child of the list item
  dom?.style.setProperty('--marker-margin-bottom', wrapperStyle.getPropertyValue('margin-bottom'))
  dom?.style.setProperty('--marker-margin-top', wrapperStyle.getPropertyValue('margin-top'))

  // NOTE: get font styles from either the innermost mark or the wrapper if that doesn't exist
  const firstLastDescendantElement = getFirstLastDescendantElement(firstRealContentDOMChild as HTMLElement)
  const textStyleElement = (firstLastDescendantElement || firstRealContentDOMChild) as HTMLElement
  const textStyle = window.getComputedStyle(textStyleElement)

  // NOTE: we don't want to inherit colors from certain nodes, so use either
  // the wrapper itself or (if that is present) the color from a `colored`
  // mark
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  const markerColor = textStyleElement.closest(IGNORED_SELECTORS)
    ? textStyleElement.closest('[data-color]')
      ? // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        textStyleElement.closest('[data-color]')!.getAttribute('data-color')
      : wrapperStyle.getPropertyValue('color')
    : textStyle.getPropertyValue('color')

  dom?.style.setProperty('--marker-font-color', markerColor)

  dom?.style.setProperty('--marker-font-size', textStyle.getPropertyValue('font-size'))
  dom?.style.setProperty('--marker-font-style', textStyle.getPropertyValue('font-style'))
  dom?.style.setProperty('--marker-font-weight', textStyle.getPropertyValue('font-weight'))

  // NOTE: line height is determined by which element has the greater line height: the wrapper or the last descendant
  const wrapperLineHeight = parseInt(wrapperStyle.getPropertyValue('line-height'))
  const textLineHeight = parseInt(textStyle.getPropertyValue('line-height'))
  dom?.style.setProperty('--marker-line-height', `${Math.max(wrapperLineHeight, textLineHeight)}px`)

  // Re-enable PM's DOMObserver.
  ;(view as any).domObserver.start()
}
